import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";
import { useAuth } from "./auth";
import { safeGet, safeSet } from "./safeStorage";

const Ctx = createContext(null);
const FALLBACK = { GBP: 1 };
const LOCALE = { USD: "en-US", EUR: "en-IE", CAD: "en-CA", AUD: "en-AU", NZD: "en-NZ", INR: "en-IN", SGD: "en-SG", HKD: "en-HK", JPY: "ja-JP", ZAR: "en-ZA", BRL: "pt-BR", MXN: "es-MX" };

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [fx, setFx] = useState({ rates: FALLBACK, date: null, supported: ["GBP"], country_currency: {} });
  const [currency, setCurrencyState] = useState(safeGet("nexus_currency") || "GBP");

  useEffect(() => { api.get("/fx").then((r) => setFx(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user?.currency) { setCurrencyState(user.currency); safeSet("nexus_currency", user.currency); } }, [user?.currency]);

  const setCurrency = useCallback((c) => { setCurrencyState(c); safeSet("nexus_currency", c); }, []);
  const rate = fx.rates?.[currency] || 1;
  const value = useMemo(() => ({
    currency, setCurrency, rate, date: fx.date, supported: fx.supported || ["GBP"], countryCurrency: fx.country_currency || {},
    isGbp: currency === "GBP",
    convert: (gbp) => (Number(gbp) || 0) * rate,
    fmt: (gbp, opts = {}) => {
      const n = (Number(gbp) || 0) * rate;
      const s = new Intl.NumberFormat(LOCALE[currency] || "en-GB", { style: "currency", currency, minimumFractionDigits: opts.decimals ?? 2, maximumFractionDigits: opts.decimals ?? 2 }).format(n);
      return currency === "GBP" || opts.exact || n === 0 ? s : `≈${s}`;
    },
    symbol: new Intl.NumberFormat(LOCALE[currency] || "en-GB", { style: "currency", currency }).formatToParts(0).find((p) => p.type === "currency")?.value || "£",
  }), [currency, setCurrency, rate, fx]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCurrency = () => useContext(Ctx) || { currency: "GBP", rate: 1, isGbp: true, fmt: (n) => `£${(Number(n) || 0).toFixed(2)}`, convert: (n) => n, setCurrency: () => {}, supported: ["GBP"], symbol: "£", countryCurrency: {} };

export const Money = ({ gbp, className = "", decimals }) => { const { fmt } = useCurrency(); return <span className={className}>{fmt(gbp, { decimals })}</span>; };

export const GbpNote = ({ className = "" }) => {
  const { isGbp, currency, date } = useCurrency();
  if (isGbp) return null;
  return <div className={`text-[11px] text-zinc-500 ${className}`} data-testid="gbp-charge-note">Payments are processed in GBP. {currency} amounts are shown at today's exchange rate{date ? ` (ECB ${date})` : ""} — your bank applies the final rate.</div>;
};

export const CurrencySelect = ({ className = "" }) => {
  const { currency, setCurrency, supported } = useCurrency();
  return (
    <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`h-8 rounded-md bg-white/5 border border-white/10 text-xs text-zinc-300 px-2 ${className}`} aria-label="Display currency" data-testid="currency-select">
      {supported.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
};
